import safeStringify from "fast-safe-stringify";

import firebase from "js/firebase";
import { serverApi } from "js/config";
import isConnected from "js/core/utilities/isConnected";
import fetchAndTrackOfflineStatus from "js/core/utilities/fetchAndTrackOfflineStatus";

import { LogGroup, LogLevel } from "js/core/logger/enums";
import LoggerTransport, { LoggerTransportEntryData } from "js/core/logger/transports/LoggerTransport";

interface ApiTransportLogError {
    name: string,
    message: string,
    stack: string
}

interface ApiTransportLogEntry<LogEntryContext> {
    timestamp: number,
    level: LogLevel,
    group: LogGroup,
    context: LogEntryContext,
    message: string,
    data: LoggerTransportEntryData,
    error?: ApiTransportLogError
}

class ApiTransport<LoggerTransportContext extends { sessionId: string }, LogEntryContext> extends LoggerTransport<LoggerTransportContext, LogEntryContext> {
    static ERROR_FLUSH_INTERVAL_MS = 2 * 1000;
    static FLUSH_INTERVAL_MS = 10 * 1000
    static MAX_ENTRIES_BEFORE_FLUSH = 100
    static MAX_ENTRIES_TO_SEND = 100
    static MAX_SERIALIZED_ENTRY_SIZE_CHARS = 10 * 1024 // approx 10kB

    private _entries: Array<ApiTransportLogEntry<LogEntryContext>> = []
    private _isFlushing: boolean = false;

    private _flushInterval: ReturnType<typeof setInterval>
    private _errorFlushInterval: ReturnType<typeof setInterval>

    constructor(context: LoggerTransportContext) {
        super(context);

        this._flushInterval = setInterval(() => this._onFlushInterval(), ApiTransport.FLUSH_INTERVAL_MS);
        this._errorFlushInterval = setInterval(() => this._onFlushInterval(true), ApiTransport.ERROR_FLUSH_INTERVAL_MS);

        window.addEventListener("beforeunload", this._onBeforeUnload);
    }

    private _onBeforeUnload = () => {
        this.dispose();
    }

    public dispose() {
        clearInterval(this._flushInterval);
        clearInterval(this._errorFlushInterval);

        window.removeEventListener("beforeunload", this._onBeforeUnload);

        this._flush(); // Fire and forget
    }

    private _onFlushInterval = (flushOnlyIfErrorsPresent = false) => {
        if (this._isFlushing) {
            // Skip flushing if currently flushing
            // to avoit clogging the network
            return;
        }

        this._flush(flushOnlyIfErrorsPresent);
    }

    private async _flush(flushOnlyIfErrorsPresent = false) {
        if (!isConnected.connected) {
            return;
        }

        if (this._entries.length === 0) {
            return;
        }

        if (flushOnlyIfErrorsPresent && !this._entries.some(entry => entry.level === LogLevel.ERROR)) {
            return;
        }

        const entriesToSend = this._entries.splice(0, ApiTransport.MAX_ENTRIES_TO_SEND);

        this._isFlushing = true;
        try {
            await this._sendEntriesToApi(entriesToSend);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[Logger][ApiTransport] _sendEntriesToApi failed, returning entries back to queue", err);
            this._entries.push(...entriesToSend);
        } finally {
            this._isFlushing = false;
        }
    }

    private async _sendEntriesToApi(entries: Array<ApiTransportLogEntry<LogEntryContext>>) {
        const body = safeStringify({
            entries: entries
                .map(entry => ({
                    ...entry,
                    context: {
                        // Combining the context of the transport and the context of the log entry
                        ...this._context,
                        ...entry.context
                    }
                }))
                .filter(entry => {
                    const serializedEntrySize = safeStringify(entry).length;
                    if (serializedEntrySize > ApiTransport.MAX_SERIALIZED_ENTRY_SIZE_CHARS) {
                        // eslint-disable-next-line no-console
                        console.error("[Logger][ApiTransport] entry size is too big, skipping", entry);
                        return false;
                    }
                    return true;
                })
        });

        // We don't use api.js to avoit circular dependency
        const fetchParams = {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "X-Client-Session-Id": this._context.sessionId
            },
            credentials: "same-origin",
            body,
            // Ensure the entries are sent when the browser window is closing
            keepalive: true
        };

        if (firebase.auth().currentUser) {
            // eslint-disable-next-line no-console
            const idToken = await firebase.auth().currentUser.getIdToken().catch(err => console.error("[Logger][ApiTransport] firebase.auth().currentUser.getIdToken() failed", err));
            if (idToken) {
                fetchParams.headers["Authorization"] = `Bearer ${idToken}`;
            }
        }

        const url = serverApi.clientLogs;

        // @ts-ignore
        const response = await fetchAndTrackOfflineStatus(url, fetchParams);

        if (!response.ok) {
            const responseBody = await response.clone().json().catch(() => null);
            // eslint-disable-next-line no-console
            console.error(`[Logger][ApiTransport] api request to ${url} failed`, {
                requestId: response.headers.get("X-Request-ID") ?? response.headers.get("x-cloud-trace-context"),
                requestBody: fetchParams.body,
                responseBody,
                url,
                fetchParams,
                status: response.status
            });

            throw new Error(`Request failed with status ${response.status}`);
        }
    }

    public log(context: LogEntryContext, group: LogGroup, level: LogLevel, message: string, data: LoggerTransportEntryData, error?: Error) {
        this._entries.push({
            timestamp: Date.now(),
            level,
            group,
            context,
            message: `[${group}] ${message}`,
            data,
            error: error ? { name: error.name ?? error.constructor?.name ?? "anonymous", message: error.message ?? "", stack: error.stack ?? "" } : null
        });

        if (this._entries.length > ApiTransport.MAX_ENTRIES_BEFORE_FLUSH) {
            this._flush();
        }
    }
}

export default ApiTransport;
